import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/browser';
import AttachFile from '@material-ui/icons/AttachFile';
import ReactS3Uploader from 'react-s3-uploader';
import Send from '@material-ui/icons/Send';
import { Input } from 'components/FormInputs';
import {
  CircularProgress,
  Fab,
  IconButton,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Errors from 'components/shared/Errors';
import api from 'api';
import * as actions from 'actions/conversation';
import { toSentryError } from 'logic/helpers';
import { isCommissionHost as isCommissionHostSelector } from 'selectors/user';
import { LOCK_REASON } from '../conversationLock';
import { handleSignError } from '../handleSignError';
import ContactsWarning from './ContactsWarning';
import AttachmentMenu from './AttachmentMenu';
import HostFileBrowserDialog from './HostFileBrowserDialog';

// Banner copy per lock reason.
// NOTE: the BLOCKED copy below is neutral placeholder wording (it deliberately
// does NOT reveal that the host was blocked for fraud) and is pending product
// sign-off on STU-75.
const LOCK_MESSAGES = {
  [LOCK_REASON.DELETED]: {
    id: 'conversations.lockedDeleted',
    defaultMessage:
      'This user has chosen to close their account. ' +
      'It is no longer possible to send messages in this conversation.',
  },
  [LOCK_REASON.BLOCKED]: {
    id: 'conversations.lockedBlocked',
    defaultMessage:
      'This host is no longer available. ' +
      'It is no longer possible to send messages in this conversation.',
  },
};

const warningRegex =
  /([a-z0-9._-]+(@|\(at\)|\[at\])[a-z0-9_-]+\.[a-z0-9]+|\+?[0-9]{3}( |-)?[0-9]{2,3}( )?[0-9]{2,})/gi;
const bankRegex =
  /bank account|bankkonto|IBAN|SMS|Swish|Paypal|Whatsapp|Messenger|Swedbank|Nordea|Handelsbanken/gi;

const MESSAGE_MAX_LENGTH = 10000;
const SENTRY_RESPONSE_MAX_CHARS = 1000;

const styles = makeStyles((theme) => ({
  chatInput: {
    marginRight: theme.spacing(2),
  },
  chatForm: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  flex: {
    flex: 1,
  },
  msgWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  progress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -18,
    marginLeft: -18,
  },
  lockNotice: {
    backgroundColor: theme.palette.grey[100],
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
  },
}));

let outer = '';

const ConversationFooter = ({
  isShowContactsWarning,
  isShowTerminationWarning,
  setInputRef,
  setFormRef,
  onFocus,
  conversationId,
  disableFileInput,
  cottageId,
  isHost,
  lockReason,
  disabled,
}) => {
  const classes = styles();
  const intl = useIntl();
  const lockMessage = lockReason ? LOCK_MESSAGES[lockReason] : null;
  const isLocked = Boolean(lockMessage);
  // `disabled` greys the controls without a banner (e.g. conversation not yet
  // loaded, or no reachable counterpart); `isLocked` additionally shows a banner.
  const inputDisabled = isLocked || disabled;
  const dispatch = useDispatch();
  const [formValues, setFormValues] = React.useState({
    message: '',
  });
  const [uploadProgress, setUploadProgress] = React.useState(false);
  const [warningSeen, setWarningSeen] = React.useState(false);
  const [uploadingFilename, setUploadingFilename] = React.useState('');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [browserOpen, setBrowserOpen] = React.useState(false);
  const [isAttaching, setIsAttaching] = React.useState(false);
  const [uploadKey, setUploadKey] = React.useState(0);
  const uploadSuccess = useSelector((state) => state.conversation.success);
  const uploadError = useSelector((state) => state.conversation.uploadError);
  const isCommissionHost = useSelector(isCommissionHostSelector);
  const contactsWarningRef = React.useRef();
  const fileInputRef = React.useRef();

  React.useEffect(() => {
    if (isAttaching && (uploadSuccess || uploadError)) {
      setIsAttaching(false);
    }
  }, [uploadSuccess, uploadError, isAttaching]);

  const storeFormValue = () => {
    localStorage.removeItem(`messageForm-${conversationId}`);

    if (outer) {
      localStorage.setItem(`messageForm-${conversationId}`, outer);
    }
  };

  React.useEffect(() => {
    outer = formValues.message;
  }, [formValues.message]);

  React.useEffect(() => {
    const stored = localStorage.getItem(`messageForm-${conversationId}`);

    if (stored && (!formValues || !formValues.message)) {
      setFormValues({ message: stored });
    }

    const _storeFormValue = storeFormValue;

    window.onbeforeunload = () => {
      _storeFormValue();

      return null;
    };

    return () => {
      storeFormValue();
    };
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();

    if (inputDisabled) {
      return;
    }

    localStorage.removeItem(`messageForm-${conversationId}`);
    const text = formValues.message.trim();

    if (!text) {
      return;
    }

    setWarningSeen(false);
    dispatch(actions.postMessageRequest({ text, conversationId }));

    setFormValues({ message: '' });
  };

  const onPreprocess = (file, next) => {
    dispatch(actions.conversationUploadFileIdle());
    if (Math.floor(file.size / 1048576) > 10) {
      // convert from bytes to MB
      alert('File size should be less than 10 MB'); // eslint-disable-line
    } else {
      setUploadingFilename(file.name);
      next(file);
    }
  };

  const onUploadProgress = (progress) => {
    setUploadProgress(progress);
  };

  const resetUploader = () => {
    setUploadProgress(false);
    setUploadKey((k) => k + 1);
  };

  const onUploadSuccess = (e) => {
    if (!e.path) {
      dispatch(
        actions.conversationUploadFileFailure({
          error: {
            message: 'Unable to upload the file.',
            canTryAgain: true,
            timestamp: Date.now(),
          },
        }),
      );
      resetUploader();
      return;
    }

    dispatch(
      actions.conversationUploadFilePostInfo({
        conversationId,
        publicUrl: e.path,
        fileName: uploadingFilename,
      }),
    );

    resetUploader();
  };

  const onUploadFailure = (err, _file, context) => {
    const { response, status, statusText, readyState } = context || {};
    Sentry.captureException(toSentryError(err, 's3-put'), {
      tags: { stage: 's3-put' },
      extra: {
        status,
        statusText,
        readyState,
        // Truncate to avoid Sentry's silent extra-payload truncation
        response:
          typeof response === 'string' ? response.slice(0, SENTRY_RESPONSE_MAX_CHARS) : response,
      },
    });
    dispatch(
      actions.conversationUploadFileFailure({
        error: {
          message: err?.message || 'Unable to upload the file.',
          canTryAgain: true,
          timestamp: Date.now(),
        },
      }),
    );
    resetUploader();
  };

  const getSignedUrl = (file, callback) => {
    // Two-arg form so callback exceptions stay unhandled instead of being
    // mistagged as sign-request failures (which a trailing .catch would do).
    api
      .getSignedUrl(conversationId, file)
      .then(callback, (err) => handleSignError(err, { dispatch, resetUploader }));
  };

  const onChange = (e) => {
    const { value } = e.target;

    if (
      isShowContactsWarning &&
      !warningSeen &&
      (warningRegex.test(value) || bankRegex.test(value))
    ) {
      contactsWarningRef.current.handlePopoverOpen(e);
      setWarningSeen(true);
    }

    setFormValues({ message: value });
  };

  const onContactsWarningCancel = () => {
    setWarningSeen(false);
    setFormValues({ message: formValues.message.replace(warningRegex, '').replace(bankRegex, '') });
  };

  const handleAttachClick = (e) => {
    if (isHost && isCommissionHost) {
      setMenuAnchorEl(e.currentTarget);
    } else {
      fileInputRef.current.click();
    }
  };

  const handleDeviceUpload = () => {
    setMenuAnchorEl(null);
    fileInputRef.current.click();
  };

  const handleOpenBrowser = () => {
    setMenuAnchorEl(null);
    setBrowserOpen(true);
  };

  const handleAttachFromLibrary = (fileId) => {
    setBrowserOpen(false);
    dispatch(actions.conversationUploadFileIdle());
    setIsAttaching(true);
    dispatch(actions.conversationAttachFromLibraryRequest({ conversationId, fileId }));
  };

  const handleRetryUpload = () => {
    dispatch(actions.conversationUploadFileIdle());
    // Re-open the file picker so the user can resubmit without hunting for the attach icon.
    fileInputRef.current?.click();
  };

  return (
    <div ref={setFormRef}>
      {isLocked && (
        <Typography variant="body2" className={classes.lockNotice}>
          {intl.formatMessage(lockMessage)}
        </Typography>
      )}
      <Toolbar>
        <form className={classes.chatForm} onSubmit={onSubmit}>
          <IconButton
            aria-label="Attach"
            onClick={handleAttachClick}
            disabled={uploadProgress !== false || isAttaching || disableFileInput || inputDisabled}
          >
            <AttachFile />
            {(uploadProgress !== false || isAttaching) && (
              <CircularProgress
                size={36}
                color="inherit"
                thickness={2}
                className={classes.progress}
              />
            )}
          </IconButton>
          <ReactS3Uploader
            getSignedUrl={getSignedUrl}
            preprocess={onPreprocess}
            onProgress={onUploadProgress}
            onError={onUploadFailure}
            onFinish={onUploadSuccess}
            uploadRequestHeaders={{ 'x-amz-acl': 'private' }}
            contentDisposition="auto"
            key={uploadKey}
            style={{ display: 'none' }}
            inputRef={(ref) => {
              fileInputRef.current = ref;
            }}
          />
          {isHost && isCommissionHost && (
            <>
              <AttachmentMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                onSelectFromLibrary={handleOpenBrowser}
                onSelectFromDevice={handleDeviceUpload}
              />
              <HostFileBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onSelect={handleAttachFromLibrary}
                cottageId={cottageId}
              />
            </>
          )}
          <Input
            name="message"
            multiline
            maxRows={8}
            disabled={inputDisabled}
            placeholder={intl.formatMessage({
              id: 'Send message',
              defaultMessage: 'Send message',
            })}
            onChange={onChange}
            className={classNames(classes.flex, classes.chatInput)}
            onFocus={onFocus}
            maxLength={MESSAGE_MAX_LENGTH}
            InputProps={{
              'aria-label': 'Input',
              ref: setInputRef,
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              maxLength: MESSAGE_MAX_LENGTH,
            }}
            errors={{}}
            value={formValues.message}
            help={`${formValues.message.length}/${MESSAGE_MAX_LENGTH}`}
            FormHelperTextProps={{
              style: {
                textAlign: 'right',
              },
            }}
          />
          <Fab
            aria-label="Send"
            color="primary"
            size="small"
            type="submit"
            disabled={!formValues.message || inputDisabled}
          >
            <Send />
          </Fab>
        </form>
      </Toolbar>
      {isShowContactsWarning && (
        <ContactsWarning
          innerRef={contactsWarningRef}
          onCancel={onContactsWarningCancel}
          terminationWarning={isShowTerminationWarning}
          isHost={isHost}
        />
      )}
      <Errors errors={uploadError} onRetry={handleRetryUpload} />
    </div>
  );
};

ConversationFooter.propTypes = {
  isShowContactsWarning: PropTypes.bool,
  isShowTerminationWarning: PropTypes.bool,
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onFocus: PropTypes.func,
  setFormRef: PropTypes.shape({}),
  setInputRef: PropTypes.shape({}),
  disableFileInput: PropTypes.bool,
  cottageId: PropTypes.number,
  isHost: PropTypes.bool,
  lockReason: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ConversationFooter;
